import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { accountingApi } from '../../../../config/apiClient';
import InventoryProductSelectionModal from '../../../accounting/inventory/components/InventoryProductSelectionModal';
import type { InventoryCategory, InventoryItem, Warehouse } from '../../../accounting/inventory/types/inventory.types';
import { formatMoney, formatQuantity } from '../../utils/productionFormat';

type OutputType = 'COPRODUCT' | 'BYPRODUCT' | 'SCRAP';
type CostMethod = 'SHARE' | 'FIXED' | 'NONE';

interface OutputRow {
  key: string;
  id?: string;
  itemId: string;
  itemName: string;
  stockCode?: string | null;
  unit: string;
  outputType: OutputType;
  quantity: number;
  costMethod: CostMethod;
  costSharePercent: number | null;
  fixedUnitValue: number | null;
  isActive: boolean;
}

interface RecipeOutputResponse {
  id: string;
  itemId: string;
  itemName?: string | null;
  stockCode?: string | null;
  unit: string;
  outputType: string;
  quantity: number;
  costMethod?: string | null;
  costSharePercent?: number | null;
  fixedUnitValue?: number | null;
  isActive: boolean;
}

interface RecipeOutputsEditorProps {
  recipeId: string;
  outputQuantity: number;
  outputUnit: string;
  mainItemId?: string;
  ingredientIds: string[];
  warehouses: Warehouse[];
  categories: InventoryCategory[];
  totalCost?: number;
  netCost?: number;
  fixedDeduction?: number;
  unitCost?: number;
  mainSharePercent?: number;
  onSaved: () => void;
}

function methodOf(outputType: string, method?: string | null): CostMethod {
  const value = (method || '').toUpperCase();
  if (value === 'SHARE' || value === 'FIXED' || value === 'NONE') return value;
  return outputType === 'COPRODUCT' ? 'SHARE' : 'NONE';
}

function asType(value: string): OutputType {
  if (value === 'COPRODUCT' || value === 'SCRAP') return value;
  return 'BYPRODUCT';
}

const RecipeOutputsEditor: React.FC<RecipeOutputsEditorProps> = ({
  recipeId,
  outputQuantity,
  outputUnit,
  mainItemId,
  ingredientIds,
  warehouses,
  categories,
  totalCost,
  netCost,
  fixedDeduction,
  unitCost,
  mainSharePercent,
  onSaved,
}) => {
  const { t } = useTranslation('production');
  const [rows, setRows] = useState<OutputRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picker, setPicker] = useState<OutputType | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    accountingApi.get(`/api/ProductRecipe/${recipeId}/outputs`)
      .then((response) => {
        const data = response.data as { outputs?: RecipeOutputResponse[] };
        setRows((data.outputs ?? []).map((line) => ({
          key: line.id,
          id: line.id,
          itemId: line.itemId,
          itemName: line.itemName || '',
          stockCode: line.stockCode,
          unit: line.unit,
          outputType: asType(line.outputType),
          quantity: line.quantity,
          costMethod: methodOf(line.outputType, line.costMethod),
          costSharePercent: line.costSharePercent ?? null,
          fixedUnitValue: line.fixedUnitValue ?? null,
          isActive: line.isActive,
        })));
        setError(null);
      })
      .catch(() => setError(t('outputs.loadFailed')))
      .finally(() => setLoading(false));
  }, [recipeId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const activeShare = useMemo(() => rows
    .filter((row) => row.isActive && row.outputType === 'COPRODUCT' && row.costMethod === 'SHARE' && row.quantity > 0)
    .reduce((sum, row) => sum + (row.costSharePercent ?? 0), 0), [rows]);

  const validate = (): string | null => {
    if (rows.length > 20) return t('outputs.maxRows');
    if (rows.some((row) => row.quantity <= 0)) return t('outputs.quantityRequired');
    if (new Set(rows.map((row) => row.itemId)).size !== rows.length) return t('outputs.duplicate');
    const active = rows.filter((row) => row.isActive);
    if (active.some((row) => row.outputType === 'COPRODUCT' && (row.costSharePercent == null || row.costSharePercent <= 0 || row.costSharePercent > 99))) {
      return t('outputs.shareRequired');
    }
    if (active.some((row) => row.outputType === 'BYPRODUCT' && row.costMethod === 'FIXED' && (row.fixedUnitValue == null || row.fixedUnitValue < 0))) {
      return t('outputs.fixedRequired');
    }
    const share = active
      .filter((row) => row.outputType === 'COPRODUCT')
      .reduce((sum, row) => sum + (row.costSharePercent ?? 0), 0);
    if (share > 99) return t('outputs.shareSum');
    return null;
  };

  const save = async () => {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await accountingApi.post(`/api/ProductRecipe/${recipeId}/outputs/save`, {
        Outputs: rows.map((row, index) => ({
          Id: row.id ?? null,
          ItemId: row.itemId,
          OutputType: row.outputType,
          Quantity: row.quantity,
          CostMethod: row.costMethod,
          CostSharePercent: row.costMethod === 'SHARE' ? row.costSharePercent : null,
          FixedUnitValue: row.costMethod === 'FIXED' ? row.fixedUnitValue : null,
          SortOrder: index,
          IsActive: row.isActive,
        })),
      });
      onSaved();
      load();
    } catch (caught) {
      const message = (caught as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message || t('errors.VALIDATION_ERROR'));
    } finally {
      setSaving(false);
    }
  };

  const addItems = (outputType: OutputType, items: InventoryItem[]) => {
    setRows((current) => [
      ...current,
      ...items.map((item) => ({
        key: crypto.randomUUID(),
        itemId: item.id,
        itemName: item.name,
        stockCode: item.stockCode,
        unit: item.unit,
        outputType,
        quantity: 1,
        costMethod: methodOf(outputType, null),
        costSharePercent: null,
        fixedUnitValue: null,
        isActive: true,
      })),
    ]);
  };

  return (
    <Box>
      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" disabled={loading || saving} onClick={() => setPicker('COPRODUCT')}>{t('outputs.coProduct')}</Button>
        <Button size="small" variant="outlined" disabled={loading || saving} onClick={() => setPicker('BYPRODUCT')}>{t('outputs.byProduct')}</Button>
        <Button size="small" variant="outlined" disabled={loading || saving} onClick={() => setPicker('SCRAP')}>{t('outputs.scrap')}</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {t('outputs.perBatch', { quantity: formatQuantity(outputQuantity), unit: outputUnit })}
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('editor.columns.material')}</TableCell>
              <TableCell>{t('editor.columns.type')}</TableCell>
              <TableCell align="right">{t('editor.quantity')}</TableCell>
              <TableCell>{t('outputs.costMethod')}</TableCell>
              <TableCell align="right">{t('outputs.valueColumn')}</TableCell>
              <TableCell align="center">{t('outputs.active')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const methods: CostMethod[] = row.outputType === 'COPRODUCT'
                ? ['SHARE']
                : row.outputType === 'BYPRODUCT'
                  ? ['FIXED', 'NONE']
                  : ['NONE'];
              return (
                <TableRow key={row.key}>
                  <TableCell>
                    <Typography variant="body2">{row.itemName}</Typography>
                    {row.stockCode ? <Typography variant="caption" color="text.secondary">{row.stockCode}</Typography> : null}
                  </TableCell>
                  <TableCell>{t(`outputs.${row.outputType}`)}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      type="number"
                      disabled={saving}
                      value={row.quantity}
                      onChange={(event) => {
                        const next = Number(event.target.value);
                        if (Number.isFinite(next)) {
                          setRows((current) => current.map((item) => (item.key === row.key ? { ...item, quantity: next } : item)));
                        }
                      }}
                      inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right', width: 88 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth disabled={saving || methods.length < 2}>
                      <Select
                        value={row.costMethod}
                        onChange={(event) => {
                          const costMethod = event.target.value as CostMethod;
                          setRows((current) => current.map((item) => (item.key === row.key ? { ...item, costMethod } : item)));
                        }}
                      >
                        {methods.map((method) => (
                          <MenuItem key={method} value={method}>
                            {method === 'SHARE' ? t('outputs.share') : method === 'FIXED' ? t('outputs.fixed') : t('outputs.none')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    {row.costMethod === 'SHARE' ? (
                      <TextField
                        size="small"
                        type="number"
                        disabled={saving}
                        value={row.costSharePercent ?? ''}
                        onChange={(event) => {
                          const next = event.target.value === '' ? null : Number(event.target.value);
                          if (next == null || Number.isFinite(next)) {
                            setRows((current) => current.map((item) => (item.key === row.key ? { ...item, costSharePercent: next } : item)));
                          }
                        }}
                        inputProps={{ min: 0, max: 99, step: 0.01, style: { textAlign: 'right', width: 72 } }}
                      />
                    ) : null}
                    {row.costMethod === 'FIXED' ? (
                      <TextField
                        size="small"
                        type="number"
                        disabled={saving}
                        value={row.fixedUnitValue ?? ''}
                        onChange={(event) => {
                          const next = event.target.value === '' ? null : Number(event.target.value);
                          if (next == null || Number.isFinite(next)) {
                            setRows((current) => current.map((item) => (item.key === row.key ? { ...item, fixedUnitValue: next } : item)));
                          }
                        }}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 88 } }}
                      />
                    ) : null}
                    {row.costMethod === 'NONE' ? '—' : null}
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={row.isActive}
                      disabled={saving}
                      onChange={(event) => {
                        const isActive = event.target.checked;
                        setRows((current) => current.map((item) => (item.key === row.key ? { ...item, isActive } : item)));
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" disabled={saving} onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack spacing={0.5} sx={{ mt: 1.5 }}>
        <Typography variant="body2">{t('outputs.mainShare', { percent: (100 - activeShare).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) })}</Typography>
        <Typography variant="body2">{t('outputs.materialTotal')}: {formatMoney(totalCost)}</Typography>
        <Typography variant="body2">{t('outputs.fixedDeduction')}: {formatMoney(fixedDeduction)}</Typography>
        <Typography variant="body2">{t('outputs.netCost')}: {formatMoney(netCost)}</Typography>
        <Typography variant="body2">{t('outputs.mainUnitCost')}: {formatMoney(unitCost, 4)}</Typography>
        {mainSharePercent != null ? (
          <Typography variant="caption" color="text.secondary">
            {t('outputs.savedShare', { percent: mainSharePercent })}
          </Typography>
        ) : null}
      </Stack>
      <Button variant="contained" sx={{ mt: 1.5 }} disabled={loading || saving} onClick={save}>
        {t('outputs.save')}
      </Button>
      <InventoryProductSelectionModal
        open={picker != null}
        onClose={() => setPicker(null)}
        title={t('outputs.add')}
        warehouses={warehouses}
        categories={categories}
        excludeItemIds={[
          ...(mainItemId ? [mainItemId] : []),
          ...ingredientIds,
          ...rows.map((row) => row.itemId),
        ]}
        excludeMenuItems
        onSelectProducts={(items) => {
          if (picker) addItems(picker, items);
          setPicker(null);
        }}
      />
    </Box>
  );
};

export default RecipeOutputsEditor;
